'use client';

import {memo, useMemo} from 'react';
import {useActiveStructure} from './frameHook';
import {ShadowDivWithForwardEvents} from './components/ShadowDivWithForwardEvents';
import {getFrameStyles} from './components/getFrameStyles';
import {getFrameInnerStyles} from './components/frameInnerStyles';
import {fixHtmlInClient} from './fixHtmlUtil';
import {useFlowThemeProperties} from '@features/flow/flowHook';
import {useAiThinkingForChat} from '@features/ai/useAiThinking';
import {cn} from '@features/ui/utils';
import z from 'zod';

export const LibraryItemFrame = memo(function LibraryItemFrame({
  chatId,
  flowId,
  isActiveFrame,
  isSelected,
}: {
  chatId: string;
  flowId: string;
  isActiveFrame: boolean;
  isSelected?: boolean;
}) {
  const {activeStructure, isFetched} = useActiveStructure(chatId);
  const themeProperties = useFlowThemeProperties(flowId, chatId);
  const isAiThinking = useAiThinkingForChat(chatId);
  const htmlRes = z.string().min(1).safeParse(activeStructure?.htmlCss);
  const htmlString = htmlRes.data;
  const html = useMemo(
    () => (htmlString ? fixHtmlInClient(htmlString) : undefined),
    [htmlString],
  );

  if (!isFetched || !html) {
    return <div className='min-w-32 rounded-lg border border-border p-6' />;
  }

  return (
    <div
      data-zoom-chrome='frame-outline'
      style={{
        outlineOffset: '0px',
        ['--outline-style' as string]: isAiThinking
          ? 'calc(2px * var(--inverse-zoom, 1)) solid var(--purple)'
          : isActiveFrame || isSelected
            ? 'calc(1px * var(--inverse-zoom, 1)) solid var(--component)'
            : 'calc(1px * var(--inverse-zoom, 1)) solid var(--border)',
      }}
      className={cn(
        'relative rounded p-6 [outline:var(--outline-style)]',
        isAiThinking && 'animate-pulse-border',
        !isAiThinking &&
          'group-hover:[outline:calc(2px*var(--inverse-zoom,1))_solid_var(--component)]',
      )}
    >
      {isAiThinking && <div className='absolute inset-0 z-[100]' />}
      <ShadowDivWithForwardEvents className='min-w-32 items-center justify-center'>
        <div
          style={getFrameStyles(themeProperties)}
          dangerouslySetInnerHTML={{__html: html}}
          data-banani-screenid={chatId}
        />
        <style children={getFrameInnerStyles(true, true)} />
      </ShadowDivWithForwardEvents>
    </div>
  );
});
